import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useScheduleRuleStore, type CreateScheduleRuleRequest, type ScheduleRuleDto } from '../../../store/useScheduleRuleStore';
import { useToastStore } from '../../../store/useToastStore';
import { Button } from '../../../components/Shared/Button';
import { Badge } from '../../../components/Shared/Badge';
import { FormField } from '../../../components/Shared/FormField';
import { Plus, Trash2, ChevronUp } from 'lucide-react';
import { ScheduleRuleTypeLabels, formatScheduleRule } from '../../../utils/scheduleRuleConstants';
import { ScheduleRuleFields } from './ScheduleRuleFields';
import { getRuleIcon, getRuleBadgeVariant } from './scheduleRuleDisplay';
import { useScheduleRuleForm } from './useScheduleRuleForm';

/**
 * A pending rule is a rule that has not been saved to the backend yet.
 * Used in "create mode" where we don't have a taskId yet.
 */
export interface PendingScheduleRule extends CreateScheduleRuleRequest {
  _localId: string;
}

interface ScheduleRuleInlineEditorProps {
  /** If provided, rules are loaded from and saved to the API (edit mode). */
  taskId?: string;
  /** For create mode: pending rules managed by parent. */
  pendingRules?: PendingScheduleRule[];
  /** For create mode: callback when pending rules change. */
  onPendingRulesChange?: (rules: PendingScheduleRule[]) => void;
}

let localIdCounter = 0;
function nextLocalId(): string {
  return `pending-rule-${++localIdCounter}`;
}

export function ScheduleRuleInlineEditor({
  taskId,
  pendingRules,
  onPendingRulesChange,
}: ScheduleRuleInlineEditorProps) {
  const { t } = useTranslation(['tasks', 'common']);
  const isEditMode = !!taskId;
  const { rules: apiRules, loading, fetchRules, createRule, deleteRule } = useScheduleRuleStore();
  const { addToast } = useToastStore();

  const [isAddOpen, setIsAddOpen] = useState(false);
  const form = useScheduleRuleForm();

  useEffect(() => {
    if (taskId) {
      fetchRules(taskId);
    }
  }, [taskId, fetchRules]);

  const closeForm = () => {
    form.reset();
    setIsAddOpen(false);
  };

  const handleAddRule = async () => {
    const data = form.buildCreate();
    if (isEditMode && taskId) {
      try {
        await createRule(taskId, data);
        addToast(t('tasks:scheduleRules.messages.created'), 'success');
      } catch {
        addToast(t('tasks:scheduleRules.messages.createError'), 'error');
        return;
      }
    } else if (onPendingRulesChange && pendingRules) {
      onPendingRulesChange([...pendingRules, { ...data, _localId: nextLocalId() }]);
    }
    closeForm();
  };

  const handleDeleteRule = async (ruleOrPending: ScheduleRuleDto | PendingScheduleRule) => {
    if (isEditMode && taskId && 'taskScheduleRuleId' in ruleOrPending) {
      try {
        await deleteRule(taskId, ruleOrPending.taskScheduleRuleId!);
        addToast(t('tasks:scheduleRules.messages.deleted'), 'success');
      } catch {
        addToast(t('tasks:scheduleRules.messages.deleteError'), 'error');
      }
    } else if (onPendingRulesChange && pendingRules && '_localId' in ruleOrPending) {
      onPendingRulesChange(pendingRules.filter((r) => r._localId !== ruleOrPending._localId));
    }
  };

  const displayRules: Array<ScheduleRuleDto | PendingScheduleRule> = isEditMode
    ? apiRules
    : (pendingRules || []);

  return (
    <FormField label={t('tasks:scheduleRules.title')}>
      <div className="space-y-2">
        {/* Existing / Pending Rules List */}
        {displayRules.length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-gray-400 italic">
            {t('tasks:scheduleRules.empty')}
          </div>
        ) : (
          <div className="space-y-2">
            {displayRules.map((rule, index) => {
              const key = 'taskScheduleRuleId' in rule ? rule.taskScheduleRuleId : ('_localId' in rule ? rule._localId : index);
              return (
                <div
                  key={key}
                  className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-gray-500 dark:text-gray-400 flex-shrink-0">
                      {getRuleIcon(rule.type, 'w-3.5 h-3.5')}
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white text-sm truncate">
                      {formatScheduleRule(rule)}
                    </span>
                    <Badge variant={getRuleBadgeVariant(rule.type)} className="flex-shrink-0">
                      {rule.type !== undefined ? ScheduleRuleTypeLabels[rule.type] || '?' : '?'}
                    </Badge>
                    {'_localId' in rule && (
                      <Badge variant="warning" className="flex-shrink-0">{t('tasks:scheduleRules.newBadge')}</Badge>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    onClick={() => handleDeleteRule(rule)}
                    disabled={loading}
                    className="flex-shrink-0 !py-1 !px-1.5"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {/* Add Rule Toggle */}
        {!isAddOpen ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setIsAddOpen(true)}
          >
            <Plus className="w-4 h-4 mr-1" />
            {t('tasks:scheduleRules.addRule')}
          </Button>
        ) : (
          <div className="border border-blue-200 dark:border-blue-800 rounded-lg p-3 bg-blue-50/50 dark:bg-blue-900/10 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('tasks:scheduleRules.newRule')}
              </span>
              <button
                type="button"
                onClick={closeForm}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                aria-label={t('common:actions.close')}
              >
                <ChevronUp className="w-4 h-4" />
              </button>
            </div>

            <ScheduleRuleFields form={form} compact />

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={closeForm}
                className="!text-xs !py-1 !px-2"
              >
                {t('tasks:scheduleRules.cancel')}
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleAddRule}
                disabled={loading}
                className="!text-xs !py-1 !px-2"
              >
                <Plus className="w-3 h-3 mr-1" />
                {t('tasks:scheduleRules.add')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </FormField>
  );
}
